import * as React from 'react';
import * as ReactDOM from 'react-dom';
import classnames from 'classnames';
import { isFunction } from 'lodash';

import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';

import { getElementDirection, getElementInfo, getElementPosition } from 'utils/element';
import { expandDataProps } from 'utils/helper/common';

import type { Data, ElementDirection, ElementTrigger, ElementTriggerClick, ElementTriggerProps } from 'types';

type Props = {
  triggerComponent?: React.ReactNode,
  children: React.ReactNode,
  trigger?: ElementTrigger,
  contentType?: 'image' | 'text' | 'mixed' | 'clear',
  classNameWrapper?: string,
  classNamePopover?: string,
  classNameTrigger?: string,
  showQuestionMark?: boolean,
  questionMarkCustomSize: {
    width: number,
    height: number
  },
  data?: Data,
  onClick?: ElementTriggerClick
};

type State = {
  x: number,
  y: number,
  message?: string,
  direction: ElementDirection,
  show: boolean,
  isMouseOverPopover: boolean
};

class Popover extends React.PureComponent<Props, State> {
  static defaultProps = {
    trigger: 'hover',
    contentType: 'mixed',
    showQuestionMark: false,
    questionMarkCustomSize: {
      width: 12,
      height: 12
    }
  };

  state: State = {
    x: 0,
    y: 0,
    direction: {
      vertical: 'bottom',
      horizontal: 'right'
    },
    show: false,
    isMouseOverPopover: false
  };

  popoverContainer = document.createElement('div');
  popoverTarget: HTMLDivElement | null = null;
  popoverElement: HTMLDivElement | null = null;
  popoverTrigger: HTMLDivElement | null = null;

  componentWillUnmount() {
    if (document.body && document.body.contains(this.popoverContainer)) {
      document.body.removeChild(this.popoverContainer);
    }
  }

  getTriggerProps(): Partial<ElementTriggerProps> {
    const { trigger } = this.props;
    const triggerProps: Partial<ElementTriggerProps> = {};

    if (trigger === 'hover') {
      triggerProps.onMouseEnter = this.handleEnter;
      triggerProps.onClick = this.handleClick;
    } else {
      triggerProps.onClick = this.handleEnter;
    }

    return triggerProps;
  }

  getHorizontalPlacement(): React.CSSProperties | null {
    const { x, direction } = this.state;
    const elementInfo = getElementInfo(this.popoverElement);

    if (direction.horizontal === 'center' && elementInfo) {
      return { left: `${x - elementInfo.width / 2}px` };
    }

    return null;
  }

  handleElementPositioning(position: number, direction: ElementDirection): void {
    const targetElementInfo = getElementInfo(this.popoverTarget);
    const elementInfo = getElementInfo(this.popoverElement);

    if (targetElementInfo && elementInfo) {
      const elementHalfWidth = elementInfo.width / 2;
      const topOffset = direction.vertical === 'top' ? -position : position;
      const leftOffset = direction.horizontal === 'center' ? targetElementInfo.width / 2 : 0;
      const rightOffset = direction.horizontal === 'left' ? -(elementInfo.width - elementHalfWidth / 2) : 0;

      this.setState({
        x: (targetElementInfo.left + window.pageXOffset) + leftOffset + rightOffset,
        y: (targetElementInfo.top + window.pageYOffset) + topOffset,
        direction,
        show: true
      });
    }
  }

  handleMouseEnter = (): void => {
    this.setState({
      isMouseOverPopover: true
    });
  };

  handleMouseLeave = (): void => {
    this.setState({
      isMouseOverPopover: false
    });
  };

  handleClick = (): void => {
    const { onClick } = this.props;
    const { isMouseOverPopover } = this.state;

    // don't open the modal when clicking on the popover itself
    if (!isMouseOverPopover && isFunction(onClick)) {
      onClick();
    }
  };

  handleEnter = (): void => {
    if (document.body && !document.body.contains(this.popoverContainer)) {
      document.body.appendChild(this.popoverContainer);
    }

    const position = getElementPosition(this.popoverTarget, this.popoverElement);
    const direction = getElementDirection(this.popoverTarget, this.popoverElement);

    if (this.popoverElement) {
      this.popoverElement.onmouseenter = this.handleMouseEnter;
      this.popoverElement.onmouseleave = this.handleMouseLeave;
    }

    if (this.popoverTrigger) {
      this.popoverTrigger.onclick = this.handleHidePopover;
    }

    if (this.popoverTarget && position && direction) {
      this.handleElementPositioning(position, direction);
    }
  };

  handleHidePopover = (): void => {
    this.setState({
      show: false
    });
  };

  renderPopover(): React.ReactNode {
    const { children, contentType, classNamePopover } = this.props;
    const { x, y, show } = this.state;
    const popoverClasses = classnames({
      'popover': true,
      'popover--visible': show,
      [`popover--type-${String(contentType)}`]: contentType,
      [`${String(classNamePopover)}`]: Boolean(classNamePopover)
    });

    const placement = {
      horizontal: this.getHorizontalPlacement()
    };

    return ReactDOM.createPortal(
      <div
        className={popoverClasses}
        style={{ top: y, left: x, ...placement.horizontal }}
        ref={ref => { this.popoverElement = ref; }}
        data-qa="popover"
      >
        {children}
      </div>,
      this.popoverContainer
    );
  }

  render(): React.ReactNode {
    const {
      triggerComponent,
      classNameTrigger,
      trigger,
      showQuestionMark,
      questionMarkCustomSize,
      classNameWrapper,
      data,
      onClick
    } = this.props;

    const { show } = this.state;

    const triggerProps = this.getTriggerProps();
    const showCursorPointer = trigger === 'click' || isFunction(onClick);

    const wrapperClassnames = classnames({
      'popover-wrapper': true,
      [`${String(classNameWrapper)}`]: Boolean(classNameWrapper)
    });

    return (
      <div
        className={wrapperClassnames}
        {...triggerProps}
        onMouseLeave={this.handleHidePopover}
        {...expandDataProps(data)}
        ref={ref => { this.popoverTarget = ref; }}
      >
        {this.renderPopover()}
        {
          Boolean(triggerComponent) && (
            <div
              className={classNameTrigger}
              ref={ref => { this.popoverTrigger = ref; }}
            >
              {triggerComponent}
            </div>
          )
        }
        {
          showQuestionMark &&
            <SVGDynamicIcon
              name="question-mark-inside-circle-custom-bg-white-fg"
              width={questionMarkCustomSize.width}
              height={questionMarkCustomSize.height}
              clickable={showCursorPointer}
              customColor={show ? 'black' : 'grey-light'}
            />
        }
      </div>
    );
  }
}

export default Popover;
